'use client';

import { ATCPageSteps } from '@features/add-to-cart-page/typings/AddToCartPageStep';
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

type Props = {
  children: ReactNode;
  steps: ATCPageSteps;
};

const AddToCartStepsContext = createContext<
  | {
      steps: ATCPageSteps;
      nextStep: () => void;
      prevStep: () => void;
      selectedStep: number;
      hasMultiStep: boolean;
    }
  | undefined
>(undefined);

// We always return true to convince memo to never re-render the component after the first render
// This is to avoid re-renders when the shopping cart updates, as the steps prop might be a new array
// reference even if the content is the same. The steps are frozen on first render anyway.
function dummyDependency() {
  return true;
}

export const AddToCartStepsProvider = memo(function AddToCartStepsProvider({
  children,
  steps: stepsProps
}: Props) {
  // Freeze the steps value on first render to prevent re-renders when shopping cart updates
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const steps = useMemo(() => stepsProps, []);
  const [currentStep, setCurrentStep] = useState(0);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  // Since steps is frozen, hasMultiStep never changes after the first render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hasMultiStep = useMemo(() => steps.length > 1, []);

  const contextValue = useMemo(
    () => ({
      steps,
      selectedStep: currentStep,
      nextStep,
      prevStep,
      hasMultiStep
    }),
    [steps, currentStep, nextStep, prevStep, hasMultiStep]
  );

  return (
    <AddToCartStepsContext.Provider value={contextValue}>{children}</AddToCartStepsContext.Provider>
  );
}, dummyDependency);

export function useAddToCartSteps() {
  const context = useContext(AddToCartStepsContext);

  if (!context) {
    throw new Error('useAddToCartSteps must be used within a AddToCartStepsProvider');
  }

  return context;
}
